<template>
  <v-card elevation="2" class="compare-models-card">
    <v-card-title>
      <span class="text-h5">Compare Models</span>
    </v-card-title>
    <v-card-text class="compare-models-card__body">
      <v-row>
        <v-col cols="12" md="6">
          <SearchableSelect
            v-model="selectedYear"
            :items="availableYears"
            label="Select Year"
            placeholder="Search year…"
          />
        </v-col>
        <v-col cols="12" md="6">
          <SearchableSelect
            v-model="selectedMake"
            :items="makeNames"
            label="Select Make"
            placeholder="Search make…"
            :disabled="!selectedYear"
            :loading="
              loading && selectedYear !== null && availableMakes.length === 0
            "
            @update:model-value="handleMakeSelection"
          />
        </v-col>
        <v-col cols="12">
          <SearchableSelect
            v-model="selectedModel"
            :items="availableModels"
            label="Select Model to Compare"
            placeholder="Search model…"
            :disabled="!selectedMake"
            :loading="loading && selectedMake !== null"
            @update:model-value="handleModelSelection"
          />
        </v-col>
      </v-row>

      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        closable
        class="mt-4"
        @click:close="errorMessage = null"
      >
        <v-alert-title>Unable to Load Model</v-alert-title>
        {{ errorMessage }}
      </v-alert>

      <template v-if="comparisonModel && comparisonTrim">
        <v-divider class="my-4"></v-divider>
        <h3 class="mb-4">Model Comparison</h3>

        <v-row>
          <v-col cols="12" md="6">
            <v-card variant="outlined">
              <v-img
                :src="currentTrim?.img || noVehicleImage"
                height="200"
                cover
              ></v-img>
              <v-card-title>
                {{ currentModel.year }} {{ currentModel.make }}
                {{ currentModel.model }}
              </v-card-title>
              <v-card-subtitle>
                {{ currentTrim?.trim || currentTrim?.styleName || 'Base Trim' }}
              </v-card-subtitle>
              <v-card-text>
                <v-list density="compact">
                  <v-list-item>
                    <v-list-item-title>Starting MSRP</v-list-item-title>
                    <v-list-item-subtitle>
                      {{
                        formatCurrency(
                          currentTrim?.baseMsrp || currentTrim?.price || 0,
                        )
                      }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Body Style</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getBodyStyle(props.currentTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Engine</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getEngineType(props.currentTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>MPG (Combined)</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getMpgInfo(props.currentTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Seating</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getSeatingCapacity(props.currentTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card variant="outlined">
              <v-img
                :src="comparisonTrim?.img || noVehicleImage"
                height="200"
                cover
              ></v-img>
              <v-card-title>
                {{ comparisonModel.year }} {{ comparisonModel.make }}
                {{ comparisonModel.model }}
              </v-card-title>
              <v-card-subtitle>
                {{
                  comparisonTrim?.trim ||
                  comparisonTrim?.styleName ||
                  'Base Trim'
                }}
              </v-card-subtitle>
              <v-card-text>
                <v-list density="compact">
                  <v-list-item>
                    <v-list-item-title>Starting MSRP</v-list-item-title>
                    <v-list-item-subtitle>
                      {{
                        formatCurrency(
                          comparisonTrim?.baseMsrp ||
                            comparisonTrim?.price ||
                            0,
                        )
                      }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Body Style</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getBodyStyle(comparisonTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Engine</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getEngineType(comparisonTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>MPG (Combined)</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getMpgInfo(comparisonTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>Seating</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ getSeatingCapacity(comparisonTrim) }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>
        <h3 class="mb-4">Key Differences</h3>
        <v-table class="comparison-table">
          <thead>
            <tr>
              <th class="text-left" style="width: 30%">Feature</th>
              <th class="text-center" style="width: 35%">
                {{ currentModel.year }} {{ currentModel.make }}
                {{ currentModel.model }}
              </th>
              <th class="text-center" style="width: 35%">
                {{ comparisonModel.year }} {{ comparisonModel.make }}
                {{ comparisonModel.model }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="diff in keyDifferences" :key="diff.feature">
              <td class="text-left">{{ diff.feature }}</td>
              <td class="text-center">
                <v-chip
                  :color="diff.currentBetter ? 'success' : 'default'"
                  variant="tonal"
                  size="small"
                >
                  {{ diff.current }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip
                  :color="diff.comparisonBetter ? 'success' : 'default'"
                  variant="tonal"
                  size="small"
                >
                  {{ diff.comparison }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-table>
      </template>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mt-4"
      ></v-progress-linear>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  fetchDataHubModel,
  fetchAvailableYears,
  fetchAvailableMakes,
  fetchAvailableModels,
} from '@/apis'
import { formatCurrency, getCDNUrl } from '@/utils'
import {
  getMpgInfo,
  getHorsepower,
  getBodyStyle,
  getEngineType,
  getSeatingCapacity,
  getDrivetrain,
} from '@/utils/modelDetails'
import SearchableSelect from '@/components/SearchableSelect.vue'

import type { TrimData, ModelData } from '@/types'
import { useModelComparisonStore } from '@/store/modelComparison'

const props = defineProps<{
  currentModel: ModelData
  currentTrim: TrimData
  selectedYear?: number | null
  selectedMake?: string | null
  selectedModel?: string | null
}>()

const emit = defineEmits<{
  'update:selectedYear': [year: number | null]
  'update:selectedMake': [make: string | null]
  'update:selectedModel': [model: string | null]
}>()

const comparisonStore = useModelComparisonStore()

const cdnUrl = getCDNUrl()
const noVehicleImage = `${cdnUrl}/lightning-model-page/cx/no-vehicle-image.png`

const loading = ref(false)
const errorMessage = ref<string | null>(null)
const selectedYear = computed({
  get: () => props.selectedYear ?? comparisonStore.selectedYear,
  set: value => {
    emit('update:selectedYear', value)
    comparisonStore.setComparisonSelection(
      value,
      selectedMake.value,
      selectedModel.value,
    )
  },
})
const selectedMake = computed({
  get: () => props.selectedMake ?? comparisonStore.selectedMake,
  set: value => {
    emit('update:selectedMake', value)
    comparisonStore.setComparisonSelection(
      selectedYear.value,
      value,
      selectedModel.value,
    )
  },
})
const selectedMakeId = ref<number | null>(null)
const selectedModel = computed({
  get: () => props.selectedModel ?? comparisonStore.selectedModel,
  set: value => {
    emit('update:selectedModel', value)
    comparisonStore.setComparisonSelection(
      selectedYear.value,
      selectedMake.value,
      value,
    )
  },
})
const comparisonModel = computed(() => comparisonStore.comparisonModel)
const comparisonTrim = computed(() => comparisonStore.comparisonTrim)

const availableYears = ref<number[]>([])
const availableMakes = ref<{ id: number; name: string }[]>([])
const availableModels = ref<string[]>([])

const makeNames = computed(() => availableMakes.value.map(m => m.name))

watch(selectedYear, async newYear => {
  if (!newYear) return

  selectedMake.value = null
  selectedMakeId.value = null
  selectedModel.value = null
  availableMakes.value = []
  availableModels.value = []
  comparisonStore.setComparisonData(null, null)
  errorMessage.value = null

  try {
    loading.value = true
    availableMakes.value = await fetchAvailableMakes(newYear)
  } catch (error) {
    console.error('Error fetching makes:', error)
  } finally {
    loading.value = false
  }
})

watch(selectedMake, async newMake => {
  if (!newMake || !selectedYear.value) return

  const modelToRestore = selectedModel.value

  selectedModel.value = null
  availableModels.value = []
  comparisonStore.setComparisonData(null, null)
  errorMessage.value = null

  const make = availableMakes.value.find(m => m.name === newMake)
  if (make) {
    selectedMakeId.value = make.id
    try {
      loading.value = true
      availableModels.value = await fetchAvailableModels(
        selectedYear.value,
        make.name,
      )

      if (modelToRestore && availableModels.value.includes(modelToRestore)) {
        selectedModel.value = modelToRestore
      }
    } catch (error) {
      console.error('Error fetching models:', error)
    } finally {
      loading.value = false
    }
  }
})

watch(selectedModel, async newModel => {
  if (!newModel) {
    comparisonStore.setComparisonData(null, null)
    return
  }

  if (selectedYear.value && selectedMake.value) {
    await fetchComparisonModel()
  }
})

const handleMakeSelection = async (value: string | number | null) => {
  const makeName = value == null ? null : String(value)
  if (makeName && makeNames.value.includes(makeName)) {
    selectedMake.value = makeName
    const make = availableMakes.value.find(m => m.name === makeName)
    if (make && selectedYear.value) {
      selectedMakeId.value = make.id
      try {
        loading.value = true
        availableModels.value = await fetchAvailableModels(
          selectedYear.value,
          make.name,
        )
      } catch (error) {
        console.error('Error fetching models:', error)
      } finally {
        loading.value = false
      }
    }
  } else if (makeName === null) {
    selectedMake.value = null
  }
}

const handleModelSelection = (value: string | number | null) => {
  const modelName = value == null ? null : String(value)
  if (modelName && availableModels.value.includes(modelName)) {
    selectedModel.value = modelName
  } else if (modelName === null) {
    selectedModel.value = null
  }
}

const keyDifferences = computed(() => {
  if (!comparisonModel.value || !comparisonTrim.value) return []

  const differences = []

  const currentPrice = Number(
    props.currentTrim?.baseMsrp || props.currentTrim?.price || 0,
  )
  const comparisonPrice = Number(
    comparisonTrim.value?.baseMsrp || comparisonTrim.value?.price || 0,
  )

  differences.push({
    feature: 'Starting Price',
    current: formatCurrency(currentPrice),
    comparison: formatCurrency(comparisonPrice),
    currentBetter: currentPrice > 0 && currentPrice < comparisonPrice,
    comparisonBetter: comparisonPrice > 0 && comparisonPrice < currentPrice,
  })

  const currentMPG = getMpgInfo(props.currentTrim)
  const comparisonMPG = getMpgInfo(comparisonTrim.value)
  const currentMPGNum = parseInt(currentMPG) || 0
  const comparisonMPGNum = parseInt(comparisonMPG) || 0

  differences.push({
    feature: 'Fuel Economy',
    current: currentMPG,
    comparison: comparisonMPG,
    currentBetter: currentMPGNum > comparisonMPGNum,
    comparisonBetter: comparisonMPGNum > currentMPGNum,
  })

  const currentHP = getHorsepower(props.currentTrim)
  const comparisonHP = getHorsepower(comparisonTrim.value)
  const currentHPNum = parseInt(currentHP) || 0
  const comparisonHPNum = parseInt(comparisonHP) || 0

  differences.push({
    feature: 'Horsepower',
    current: currentHP,
    comparison: comparisonHP,
    currentBetter: currentHPNum > comparisonHPNum,
    comparisonBetter: comparisonHPNum > currentHPNum,
  })

  const currentSeating = getSeatingCapacity(props.currentTrim)
  const comparisonSeating = getSeatingCapacity(comparisonTrim.value)

  differences.push({
    feature: 'Seating Capacity',
    current: currentSeating,
    comparison: comparisonSeating,
    currentBetter: false,
    comparisonBetter: false,
  })

  const currentDrivetrain = getDrivetrain(props.currentTrim)
  const comparisonDrivetrain = getDrivetrain(comparisonTrim.value)

  differences.push({
    feature: 'Drivetrain',
    current: currentDrivetrain,
    comparison: comparisonDrivetrain,
    currentBetter: false,
    comparisonBetter: false,
  })

  return differences
})

const fetchComparisonModel = async () => {
  if (!selectedYear.value || !selectedMake.value || !selectedModel.value) return

  try {
    loading.value = true
    errorMessage.value = null
    const modelName = selectedModel.value.trim()
    const response = await fetchDataHubModel(
      selectedYear.value,
      selectedMake.value,
      modelName,
    )

    if (response) {
      comparisonStore.setComparisonData(response, response.trims?.[0] || null)
    } else {
      throw new Error('No data returned for the selected model')
    }
  } catch (error) {
    console.error('Error fetching comparison model:', error)
    comparisonStore.setComparisonData(null, null)
    errorMessage.value = `Failed to load ${selectedYear.value} ${selectedMake.value} ${selectedModel.value}. ${error instanceof Error ? error.message : 'Please try a different model.'}`
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  try {
    loading.value = true

    // If we already have comparison data in store, we can use it immediately
    // but we still need to load the dropdown options
    if (
      comparisonStore.comparisonModel &&
      comparisonStore.comparisonTrim &&
      comparisonStore.selectedYear &&
      comparisonStore.selectedMake &&
      comparisonStore.selectedModel
    ) {
      // Restore dropdowns for the selected values
      availableYears.value = await fetchAvailableYears()
      if (comparisonStore.selectedYear) {
        availableMakes.value = await fetchAvailableMakes(
          comparisonStore.selectedYear,
        )
        const makeObj = availableMakes.value.find(
          m => m.name === comparisonStore.selectedMake,
        )
        if (makeObj) {
          selectedMakeId.value = makeObj.id
          availableModels.value = await fetchAvailableModels(
            comparisonStore.selectedYear,
            makeObj.name,
          )
        }
      }
      // The computed properties will automatically use store values
      // and comparisonModel/comparisonTrim are already computed from store
      loading.value = false
      return
    }

    availableYears.value = await fetchAvailableYears()

    // Restore from store or props
    const yearToSet =
      props.selectedYear ||
      comparisonStore.selectedYear ||
      (props.currentModel?.year ? Number(props.currentModel.year) : null)

    if (yearToSet) {
      availableMakes.value = await fetchAvailableMakes(yearToSet)
      selectedYear.value = yearToSet

      const makeToSet = props.selectedMake || comparisonStore.selectedMake
      if (makeToSet) {
        const makeObj = availableMakes.value.find(m => m.name === makeToSet)
        if (makeObj) {
          selectedMakeId.value = makeObj.id
          availableModels.value = await fetchAvailableModels(
            yearToSet,
            makeObj.name,
          )
          selectedMake.value = makeToSet

          const modelToSet =
            props.selectedModel || comparisonStore.selectedModel
          if (modelToSet && availableModels.value.includes(modelToSet)) {
            selectedModel.value = modelToSet
            // Fetch if we have a model but no data
            if (
              !comparisonStore.comparisonModel ||
              !comparisonStore.comparisonTrim
            ) {
              await fetchComparisonModel()
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('Error initializing comparison:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.compare-models-card,
.compare-models-card__body,
.compare-models-card :deep(.v-card-item),
.compare-models-card :deep(.v-card-text),
.compare-models-card :deep(.v-row),
.compare-models-card :deep(.v-col) {
  overflow: visible !important;
}

:deep(.v-list-item-subtitle) {
  white-space: normal;
  opacity: 0.9;
}

:deep(.v-chip) {
  font-weight: 500;
}

.comparison-table {
  width: 100%;
}

.comparison-table :deep(thead th) {
  font-weight: 600;
  padding: 12px 16px;
  border-bottom: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.comparison-table :deep(tbody td) {
  padding: 12px 16px;
  vertical-align: middle;
}

.comparison-table :deep(tbody tr) {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.comparison-table :deep(tbody tr:hover) {
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}
</style>
